/**
 * Schnellerfassung (SPEC 9.5).
 *
 * `Müll rausbringen @lena morgen !hoch #haushalt` wird zu einer Karte mit
 * Verantwortlichem, Faelligkeit, Prioritaet und Label.
 *
 * Reine Funktion, ohne Uhr und ohne Zeitzone aus der Umgebung: `now` kommt
 * von aussen. Nur so laesst sich „morgen" testen, ohne dass der Test am
 * 31. Dezember umkippt.
 *
 * Dasselbe Modul laeuft im Browser fuer die Vorschau und auf dem Server beim
 * Anlegen. Zwei Auswertungen, die auseinanderlaufen koennen, waeren die
 * unangenehmste Art von Fehler: man sieht etwas anderes, als man bekommt.
 */
const WEEKDAY_NAMES = {
    montag: 0,
    dienstag: 1,
    mittwoch: 2,
    donnerstag: 3,
    freitag: 4,
    samstag: 5,
    sonnabend: 5,
    sonntag: 6,
};
const PRIORITY_WORDS = {
    keine: 0,
    niedrig: 1,
    mittel: 2,
    hoch: 3,
};
/** Ohne Uhrzeit gilt eine Karte bis Tagesende — wie im Kartendetail auch. */
const END_OF_DAY = { hours: 23, minutes: 59 };
function startOfDay(date) {
    return { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() };
}
function shiftDays(from, days) {
    const moved = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
    return startOfDay(moved);
}
/*
 * Wortgrenzen, die Umlaute mitzaehlen.
 *
 * `\b` kennt nur ASCII: vor dem „ü" von „übermorgen" steht damit keine
 * Wortgrenze, und der Ausdruck trifft nie. Ein Fehler, der ausgerechnet die
 * deutschen Woerter uebersieht — also stattdessen Unicode-Lookarounds.
 */
const VOR = '(?<![\\p{L}\\p{N}])';
const NACH = '(?![\\p{L}\\p{N}])';
/**
 * Die Erkenner, in der Reihenfolge ihrer Genauigkeit.
 *
 * Wichtig ist nur die Trennung von Datum und Uhrzeit bei Punkten: `24.12.`
 * ist ein Datum (abschliessender Punkt), `14.30 Uhr` eine Uhrzeit (das Wort
 * „Uhr" ist Pflicht). Ohne diese Regel wird aus „14.30" mal das eine, mal das
 * andere — je nachdem, welcher Erkenner zuerst drankommt.
 */
const MATCHERS = [
    {
        kind: 'date',
        pattern: new RegExp(`${VOR}(\\d{1,2})\\.(\\d{1,2})\\.(\\d{4})?`, 'giu'),
        build: (m) => (state, now) => {
            const day = Number(m[1]);
            const month = Number(m[2]) - 1;
            const year = m[3] ? Number(m[3]) : now.getFullYear();
            const candidate = new Date(year, month, day);
            if (candidate.getMonth() !== month || candidate.getDate() !== day)
                return;
            /*
             * Ohne Jahresangabe ist ein Datum gemeint, das noch kommt. „24.12."
             * im Januar waere sonst elf Monate in der Vergangenheit und die Karte
             * sofort ueberfaellig.
             */
            if (!m[3] && candidate.getTime() < new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) {
                state.date = { year: year + 1, month, day };
                return;
            }
            state.date = { year, month, day };
        },
    },
    {
        kind: 'time',
        pattern: new RegExp(`${VOR}(?:um\\s+)?(\\d{1,2})[:.](\\d{2})\\s*(?:uhr)?${NACH}`, 'giu'),
        build: (m) => (state) => {
            const hours = Number(m[1]);
            const minutes = Number(m[2]);
            if (hours > 23 || minutes > 59)
                return;
            state.time = { hours, minutes };
        },
    },
    {
        kind: 'time',
        pattern: new RegExp(`${VOR}(?:um\\s+)?(\\d{1,2})\\s*uhr${NACH}`, 'giu'),
        build: (m) => (state) => {
            const hours = Number(m[1]);
            if (hours > 23)
                return;
            state.time = { hours, minutes: 0 };
        },
    },
    {
        kind: 'date',
        pattern: new RegExp(`${VOR}in\\s+(\\d{1,3})\\s+(tag|tagen|woche|wochen)${NACH}`, 'giu'),
        build: (m) => (state, now) => {
            const amount = Number(m[1]);
            const days = m[2].toLowerCase().startsWith('woche') ? amount * 7 : amount;
            state.date = shiftDays(now, days);
        },
    },
    {
        kind: 'date',
        pattern: new RegExp(`${VOR}(übermorgen|uebermorgen)${NACH}`, 'giu'),
        build: () => (state, now) => {
            state.date = shiftDays(now, 2);
        },
    },
    {
        kind: 'date',
        pattern: new RegExp(`${VOR}morgen${NACH}`, 'giu'),
        build: () => (state, now) => {
            state.date = shiftDays(now, 1);
        },
    },
    {
        kind: 'date',
        pattern: new RegExp(`${VOR}heute${NACH}`, 'giu'),
        build: () => (state, now) => {
            state.date = startOfDay(now);
        },
    },
    {
        kind: 'date',
        pattern: new RegExp(`${VOR}(nächsten|naechsten|kommenden)?\\s*(montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonnabend|sonntag)${NACH}`, 'giu'),
        build: (m) => (state, now) => {
            const target = WEEKDAY_NAMES[m[2].toLowerCase()];
            if (target === undefined)
                return;
            const current = (now.getDay() + 6) % 7;
            /*
             * „Montag" an einem Montag meint heute — so redet man. „Nächsten
             * Montag" meint die Woche darauf.
             */
            let ahead = (target - current + 7) % 7;
            if (m[1])
                ahead += 7;
            state.date = shiftDays(now, ahead);
        },
    },
    {
        kind: 'priority',
        pattern: new RegExp(`!(keine|niedrig|mittel|hoch)${NACH}`, 'giu'),
        build: (m) => (state) => {
            state.priority = PRIORITY_WORDS[m[1].toLowerCase()] ?? 0;
        },
    },
    {
        kind: 'assignee',
        pattern: /@([\p{L}\p{N}_-]+)/gu,
        build: (m) => (state) => {
            if (!state.assigneeNames.includes(m[1]))
                state.assigneeNames.push(m[1]);
        },
    },
    {
        kind: 'label',
        pattern: /#([\p{L}\p{N}_-]+)/gu,
        build: (m) => (state) => {
            if (!state.labelNames.includes(m[1]))
                state.labelNames.push(m[1]);
        },
    },
];
export function parseQuickCapture(input, now = new Date()) {
    const candidates = [];
    for (const matcher of MATCHERS) {
        // Eigene Instanz je Durchlauf: ein geteiltes `lastIndex` ueberspringt
        // Treffer, sobald dieselbe Regex zweimal benutzt wird.
        const pattern = new RegExp(matcher.pattern.source, matcher.pattern.flags);
        let match;
        while ((match = pattern.exec(input)) !== null) {
            if (match[0].trim() === '') {
                pattern.lastIndex += 1;
                continue;
            }
            candidates.push({
                kind: matcher.kind,
                start: match.index,
                end: match.index + match[0].length,
                text: match[0],
                apply: matcher.build(match),
            });
        }
    }
    /*
     * Ueberschneidungen aufloesen: der frueheste Treffer gewinnt, bei
     * gleichem Anfang der laengere. Damit schlaegt „nächsten Montag" das
     * blosse „Montag" und „24.12.2026" das kuerzere „24.12.".
     */
    candidates.sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start));
    const tokens = [];
    const accepted = [];
    let cursor = -1;
    for (const candidate of candidates) {
        if (candidate.start < cursor)
            continue;
        accepted.push(candidate);
        tokens.push({
            kind: candidate.kind,
            start: candidate.start,
            end: candidate.end,
            text: candidate.text,
        });
        cursor = candidate.end;
    }
    const state = {
        date: null,
        time: null,
        priority: 0,
        assigneeNames: [],
        labelNames: [],
    };
    for (const match of accepted)
        match.apply(state, now);
    // Titel ist, was zwischen den Treffern uebrig bleibt.
    let title = '';
    let last = 0;
    for (const token of tokens) {
        title += input.slice(last, token.start);
        last = token.end;
    }
    title += input.slice(last);
    title = title.replace(/\s+/g, ' ').trim();
    let dueAt = null;
    let dueHasTime = false;
    if (state.date) {
        const { hours, minutes } = state.time ?? END_OF_DAY;
        dueAt = new Date(state.date.year, state.date.month, state.date.day, hours, minutes, 0, 0);
        dueHasTime = state.time !== null;
    }
    else if (state.time) {
        /*
         * Nur eine Uhrzeit ohne Tag: heute, und wenn das schon vorbei ist,
         * morgen. „18 Uhr" um 20 Uhr gesagt meint nicht heute Abend.
         */
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), state.time.hours, state.time.minutes, 0, 0);
        dueAt = today.getTime() > now.getTime() ? today : new Date(today.getTime() + 86_400_000);
        dueHasTime = true;
    }
    return {
        title,
        dueAt,
        dueHasTime,
        priority: state.priority,
        assigneeNames: state.assigneeNames,
        labelNames: state.labelNames,
        tokens: tokens.sort((a, b) => a.start - b.start),
    };
}
//# sourceMappingURL=quick-capture.js.map